import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AudioLines, Loader2, Square, Volume2 } from 'lucide-react';
import { cn, ui } from '../../utils';
import { useChatStore } from '../../stores/chatStore';
import {
  probeVoiceAvailability,
  VoiceSessionController,
  type VoiceError,
  type VoiceSessionState,
  type VoiceTranscriptEvent,
} from '../../services/voiceMode';
import type { Message } from '../../types';

/**
 * VoiceModeButton — Ally Voice Mode (pilot, feature-flagged).
 *
 * Real-time speech-to-speech with the agent over WebRTC (OpenAI Realtime via
 * server-minted ephemeral tokens). Distinct from the legacy VoiceButton
 * (record → transcribe → send as text), which it sits alongside during the
 * pilot.
 *
 * ZERO FOOTPRINT when the feature flag is off or the user is not in the
 * pilot allow-list: the availability probe (/api/config → voice.enabled)
 * resolves false and this component renders NOTHING — no icon, no tooltip,
 * no rollout tells (spec §6.5).
 *
 * States: idle (mic-waves icon) → connecting (spinner) → live (pulse dot +
 * animated waveform + timer + end button) → error (inline message, auto
 * returns to idle). Final transcripts land in the chat thread as voice-badged
 * messages via chatStore.addMessage.
 */

const ERROR_DISMISS_MS = 6000;

interface VoiceModeButtonProps {
  conversationId: string;
  agentId?: string | null;
  disabled?: boolean;
  className?: string;
}

function formatElapsed(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function VoiceModeButton({ conversationId, agentId = null, disabled = false, className }: VoiceModeButtonProps) {
  const [available, setAvailable] = useState(false);
  const [sessionState, setSessionState] = useState<VoiceSessionState>('idle');
  const [error, setError] = useState<VoiceError | null>(null);
  const [agentSpeaking, setAgentSpeaking] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const controllerRef = useRef<VoiceSessionController | null>(null);
  const conversationIdRef = useRef(conversationId);
  const agentIdRef = useRef(agentId);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addMessage = useChatStore((state) => state.addMessage);

  conversationIdRef.current = conversationId;
  agentIdRef.current = agentId;

  // Availability probe: render nothing until (and unless) the server says
  // this user is in the voice pilot.
  useEffect(() => {
    let cancelled = false;
    void probeVoiceAvailability().then((enabled) => {
      if (!cancelled) setAvailable(enabled);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleTranscript = useCallback((event: VoiceTranscriptEvent) => {
    if (!event.final) {
      return; // live partials stay in the control bar (agentSpeaking pulse)
    }
    const message: Message = {
      id: `voice-${event.kind}-${event.itemId}`,
      conversationId: conversationIdRef.current,
      role: event.kind === 'user' ? 'user' : 'assistant',
      content: event.text,
      metadata: { source: 'voice' },
      createdAt: new Date().toISOString(),
      status: 'sent',
      ...(event.kind === 'agent' && agentIdRef.current ? { agentId: agentIdRef.current } : {}),
    };
    addMessage(message);
  }, [addMessage]);

  const getController = useCallback(() => {
    if (!controllerRef.current) {
      controllerRef.current = new VoiceSessionController({
        onStateChange: (state, stateError) => {
          setSessionState(state);
          setError(stateError);
          if (state !== 'live') {
            setAgentSpeaking(false);
          }
        },
        onTranscript: handleTranscript,
        onAgentSpeaking: setAgentSpeaking,
      });
    }
    return controllerRef.current;
  }, [handleTranscript]);

  // Elapsed timer while live.
  useEffect(() => {
    if (sessionState !== 'live') {
      setElapsedSeconds(0);
      return;
    }
    const startedAt = Date.now();
    const timer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [sessionState]);

  // Errors show inline for a few seconds, then the button resets to idle.
  useEffect(() => {
    if (sessionState !== 'error') {
      return;
    }
    errorTimerRef.current = setTimeout(() => {
      controllerRef.current?.reset();
    }, ERROR_DISMISS_MS);
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, [sessionState]);

  // Unmount: tear the session down (controller also ends server usage).
  useEffect(() => () => {
    controllerRef.current?.destroy();
    controllerRef.current = null;
  }, []);

  const handleStart = useCallback(() => {
    if (disabled) return;
    const controller = getController();
    controller.reset();
    void controller.start({
      agentId: agentIdRef.current,
      conversationId: conversationIdRef.current,
    });
  }, [disabled, getController]);

  const handleEnd = useCallback(() => {
    void controllerRef.current?.end('user_ended');
  }, []);

  if (!available) {
    return null;
  }

  const isConnecting = sessionState === 'requesting_mic' || sessionState === 'connecting';
  const isLive = sessionState === 'live' || sessionState === 'ending';

  if (isLive) {
    return (
      <div
        className={cn(
          'flex min-h-[44px] items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1.5',
          className,
        )}
        data-testid="voice-mode-live"
      >
        <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 motion-safe:animate-ping" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
        </span>

        {agentSpeaking ? (
          <Volume2 className="h-4 w-4 text-emerald-200 motion-safe:animate-pulse" aria-hidden="true" />
        ) : (
          <span className="flex items-end gap-0.5" aria-hidden="true">
            {[0, 1, 2].map((bar) => (
              <span
                key={bar}
                className="w-0.5 rounded-full bg-emerald-300/80 motion-safe:animate-pulse"
                style={{ height: `${8 + bar * 3}px`, animationDelay: `${bar * 150}ms` }}
              />
            ))}
          </span>
        )}

        <span className="font-mono text-xs text-emerald-200" data-testid="voice-mode-timer">
          {formatElapsed(elapsedSeconds)}
        </span>

        <span className="sr-only" aria-live="polite">
          {agentSpeaking ? 'Agent is speaking' : 'Voice mode active'}
        </span>

        <button
          type="button"
          onClick={handleEnd}
          disabled={sessionState === 'ending'}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-lg bg-red-500/20 text-red-200 transition-colors duration-150 hover:bg-red-500/30',
            'disabled:cursor-not-allowed disabled:opacity-60',
          )}
          title="End voice mode"
          aria-label="End voice mode"
        >
          <Square className="h-3.5 w-3.5" fill="currentColor" />
        </button>
      </div>
    );
  }

  return (
    <div className={cn('relative', className)}>
      {sessionState === 'error' && error ? (
        <div
          role="status"
          className="absolute bottom-full right-0 z-50 mb-2 w-64 rounded-xl border border-red-500/30 bg-card p-3 text-xs text-red-200 shadow-xl shadow-black/30"
          data-testid="voice-mode-error"
        >
          {error.message}
        </div>
      ) : null}

      <button
        type="button"
        onClick={isConnecting ? undefined : handleStart}
        disabled={disabled || isConnecting}
        className={cn(
          ui.button.icon,
          isConnecting && 'text-blue-300',
          sessionState === 'error' && 'text-red-300',
        )}
        title="Voice mode"
        aria-label={isConnecting ? 'Connecting voice mode' : 'Start voice mode'}
        aria-busy={isConnecting}
        data-testid="voice-mode-button"
      >
        {isConnecting ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <AudioLines className="h-5 w-5" />
        )}
      </button>
    </div>
  );
}

export default VoiceModeButton;
